import type { ReactNode } from 'react'
import {
  $applyNodeReplacement,
  DecoratorNode,
  type DOMConversionMap,
  type DOMConversionOutput,
  type DOMExportOutput,
  type EditorConfig,
  type ElementFormatType,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from 'lexical'
import { ImageComponent } from './image-component'

export type SerializedImageNode = Spread<
  {
    src: string
    altText: string
    width?: number
    height?: number
    format?: ElementFormatType
  },
  SerializedLexicalNode
>

function convertImageElement(domNode: Node): DOMConversionOutput | null {
  if (domNode instanceof HTMLImageElement) {
    const src = domNode.getAttribute('src')
    if (!src) return null
    const altText = domNode.getAttribute('alt') ?? ''
    const width = domNode.width || undefined
    const height = domNode.height || undefined
    const node = $createImageNode({ src, altText, width, height })
    return { node }
  }
  return null
}

const FORMAT_TO_ALIGN: Record<number, string> = {
  1: 'left',
  2: 'center',
  3: 'right',
  4: 'justify',
  5: 'left',
  6: 'right',
}

const ALIGN_TO_FORMAT: Record<string, number> = {
  left: 1,
  center: 2,
  right: 3,
  justify: 4,
  start: 5,
  end: 6,
}

const FORMAT_TO_TYPE: Record<number, ElementFormatType> = {
  1: 'left',
  2: 'center',
  3: 'right',
  4: 'justify',
  5: 'start',
  6: 'end',
}

export class ImageNode extends DecoratorNode<ReactNode> {
  __src: string
  __altText: string
  __width: number | undefined
  __height: number | undefined
  __format: number

  static getType(): string {
    return 'image'
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__width,
      node.__height,
      node.__format,
      node.__key
    )
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    }
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const node = $createImageNode({
      src: serializedNode.src,
      altText: serializedNode.altText,
      width: serializedNode.width,
      height: serializedNode.height,
    })
    if (serializedNode.format) {
      node.setFormat(serializedNode.format)
    }
    return node
  }

  constructor(
    src: string = '',
    altText: string = '',
    width?: number,
    height?: number,
    format: number = 0,
    key?: NodeKey
  ) {
    super(key)
    this.__src = src
    this.__altText = altText
    this.__width = width
    this.__height = height
    this.__format = format
  }

  exportJSON(): SerializedImageNode {
    return {
      ...super.exportJSON(),
      type: 'image',
      version: 1,
      src: this.__src,
      altText: this.__altText,
      width: this.__width,
      height: this.__height,
      format: this.getFormatType(),
    }
  }

  exportDOM(): DOMExportOutput {
    const container = document.createElement('div')
    container.style.textAlign = FORMAT_TO_ALIGN[this.__format] ?? 'left'

    const img = document.createElement('img')
    img.setAttribute('src', this.__src)
    img.setAttribute('alt', this.__altText)
    if (this.__width) img.setAttribute('width', String(this.__width))
    if (this.__height) img.setAttribute('height', String(this.__height))
    container.appendChild(img)
    return { element: container }
  }

  createDOM(config: EditorConfig): HTMLElement {
    const div = document.createElement('div')
    const theme = config.theme
    const className = theme.image
    if (className) {
      div.className = className
    }
    div.style.textAlign = FORMAT_TO_ALIGN[this.__format] ?? 'left'
    return div
  }

  updateDOM(prevNode: ImageNode): boolean {
    return prevNode.__format !== this.__format
  }

  decorate(): ReactNode {
    return (
      <ImageComponent
        src={this.__src}
        altText={this.__altText}
        width={this.__width}
        height={this.__height}
        nodeKey={this.__key}
      />
    )
  }

  setDimensions(width: number | undefined, height: number | undefined): void {
    const writable = this.getWritable()
    writable.__width = width
    writable.__height = height
  }

  setFormat(format: ElementFormatType | number): this {
    const writable = this.getWritable()
    writable.__format = typeof format === 'string' ? (ALIGN_TO_FORMAT[format] ?? 0) : format
    return writable
  }

  getFormatType(): ElementFormatType {
    return FORMAT_TO_TYPE[this.getLatest().__format] ?? ''
  }

  isInline(): boolean {
    return false
  }
}

export function $createImageNode({
  src,
  altText = '',
  width,
  height,
}: {
  src: string
  altText?: string
  width?: number
  height?: number
}): ImageNode {
  return $applyNodeReplacement(new ImageNode(src, altText, width, height))
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode
}
